import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface SplitContainerProps {
  split: number
  onSplitChange: (pct: number) => void
  primary: ReactNode
  secondary?: ReactNode
  /** Layout breakpoint in px — orientation becomes 'h' at/above this width. Default 768. */
  breakpointPx?: number
  appearance?: 'legacy' | 'v3'
  className?: string
}

const MIN = 25
const MAX = 75

function clamp(pct: number): number {
  return Math.min(MAX, Math.max(MIN, Math.round(pct)))
}

function useIsWide(breakpointPx: number): boolean {
  const [wide, setWide] = useState<boolean>(() =>
    typeof window === 'undefined'
      ? true
      : window.matchMedia(`(min-width: ${breakpointPx}px)`).matches,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia(`(min-width: ${breakpointPx}px)`)
    const onChange = (e: MediaQueryListEvent) => setWide(e.matches)
    mql.addEventListener('change', onChange)
    setWide(mql.matches)
    return () => mql.removeEventListener('change', onChange)
  }, [breakpointPx])
  return wide
}

export function SplitContainer({
  split,
  onSplitChange,
  primary,
  secondary,
  breakpointPx = 768,
  appearance = 'legacy',
  className,
}: SplitContainerProps) {
  const isWide = useIsWide(breakpointPx)
  const orientation: 'h' | 'v' = isWide ? 'h' : 'v'

  return (
    <div
      className={cn(
        'flex flex-col w-full h-full min-h-0 motion-reduce:transition-none',
        className,
      )}
      data-testid="split-container"
    >
      <Body
        orientation={orientation}
        split={split}
        onSplitChange={onSplitChange}
        primary={primary}
        secondary={secondary}
        appearance={appearance}
      />
    </div>
  )
}

function Body({
  orientation,
  split,
  onSplitChange,
  primary,
  secondary,
  appearance,
}: {
  orientation: 'h' | 'v'
  split: number
  onSplitChange: (pct: number) => void
  primary: ReactNode
  secondary?: ReactNode
  appearance: 'legacy' | 'v3'
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const activePointerIdRef = useRef<number | null>(null)

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    activePointerIdRef.current = e.pointerId
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }, [])

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activePointerIdRef.current !== e.pointerId) return
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      const pct =
        orientation === 'h'
          ? ((e.clientX - rect.left) / rect.width) * 100
          : ((e.clientY - rect.top) / rect.height) * 100
      onSplitChange(clamp(pct))
    },
    [orientation, onSplitChange],
  )

  const finishDragging = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== e.pointerId) return
    activePointerIdRef.current = null
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    }
  }, [])

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => finishDragging(e),
    [finishDragging],
  )

  const onPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => finishDragging(e),
    [finishDragging],
  )

  const onLostPointerCapture = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => finishDragging(e),
    [finishDragging],
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const big = e.shiftKey
      let next: number | null = null
      if (orientation === 'h') {
        if (e.key === 'ArrowLeft') next = split - (big ? 10 : 1)
        else if (e.key === 'ArrowRight') next = split + (big ? 10 : 1)
      } else {
        if (e.key === 'ArrowUp') next = split - (big ? 10 : 1)
        else if (e.key === 'ArrowDown') next = split + (big ? 10 : 1)
      }
      if (e.key === 'Home') next = MIN
      else if (e.key === 'End') next = MAX
      else if (e.key === 'Enter') next = 50
      if (next !== null) {
        e.preventDefault()
        onSplitChange(clamp(next))
      }
    },
    [orientation, split, onSplitChange],
  )

  const onDoubleClick = useCallback(() => onSplitChange(50), [onSplitChange])

  const hasSecondary = secondary != null
  const separatorHalf = appearance === 'v3' ? 4 : 8
  const primaryStyle = hasSecondary
    ? orientation === 'h'
      ? { width: `calc(${split}% - ${separatorHalf}px)` }
      : { height: `calc(${split}% - ${separatorHalf}px)` }
    : { width: '100%', height: '100%' }
  const secondaryStyle =
    orientation === 'h'
      ? { width: `calc(${100 - split}% - ${separatorHalf}px)` }
      : { height: `calc(${100 - split}% - ${separatorHalf}px)` }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex-1 min-h-0 flex',
        orientation === 'h' ? 'flex-row' : 'flex-col',
      )}
    >
      <div className="min-w-0 min-h-0 overflow-hidden" style={primaryStyle}>
        {primary}
      </div>

      {hasSecondary && (
        <>
          <div
            role="separator"
            aria-orientation={orientation === 'h' ? 'vertical' : 'horizontal'}
            aria-valuenow={split}
            aria-valuemin={MIN}
            aria-valuemax={MAX}
            tabIndex={0}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            onLostPointerCapture={onLostPointerCapture}
            onKeyDown={onKeyDown}
            onDoubleClick={onDoubleClick}
            className={cn(
              'group relative z-10 flex items-center justify-center shrink-0 touch-none select-none',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-lightblue-900',
              orientation === 'h'
                ? appearance === 'v3'
                  ? 'w-6 -mx-2 cursor-col-resize'
                  : 'w-4 cursor-col-resize hover:bg-lc-gray-100'
                : appearance === 'v3'
                  ? 'h-6 -my-2 cursor-row-resize'
                  : 'h-4 cursor-row-resize hover:bg-lc-gray-100',
            )}
          >
            <span
              aria-hidden
              className={cn(
                'block rounded-full transition-colors motion-reduce:transition-none',
                appearance === 'v3'
                  ? 'bg-lc-v3-gray-500 group-hover:bg-lc-key-500'
                  : 'bg-lc-gray-300 group-hover:bg-lc-lightblue-900',
                orientation === 'h'
                  ? appearance === 'v3' ? 'h-20 w-2' : 'h-16 w-1.5'
                  : appearance === 'v3' ? 'h-2 w-20' : 'h-1.5 w-16',
              )}
            />
          </div>

          <div className="min-w-0 min-h-0 overflow-hidden" style={secondaryStyle}>
            {secondary}
          </div>
        </>
      )}
    </div>
  )
}
